import React, { FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Lock, LogIn, User } from 'lucide-react';
import { sanitizeWorkspaceSlug, sanitizeWorkspaceSlugInput } from '../utils/workspace';

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

interface LoginPageProps {
  isDarkMode: boolean;
}

const safeRedirectPath = (value: string | null): string => {
  const trimmed = (value || '').trim();
  if (!trimmed || !trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return '';
  }
  return trimmed;
};

const sessionFromAuthPayload = (data: any) => ({
  username: data.username || null,
  user_id: data.user?.id || data.user_id || null,
  role: data.role || null,
  platform_role: data.platform_role || null,
  is_superadmin: data.role === 'superadmin' || data.platform_role === 'superadmin' || data.platform_role === 'owner',
  has_active_customeradmin: Boolean(
    data.has_active_customeradmin
    || data.customer_memberships?.some((membership: any) => (
      membership?.role === 'customeradmin' && membership?.status === 'active'
    )),
  ),
  active_customer_id: data.active_customer_id || data.customer?.id || null,
  active_customer_slug: data.active_customer_slug || data.customer?.slug || null,
  active_customer_name: data.active_customer_name || data.customer?.name || data.customer_memberships?.[0]?.customer_name || null,
  active_workspace_slug: data.active_workspace_slug || data.workspace?.slug || null,
  active_workspace_name: data.active_workspace_name || data.workspace?.name || null,
  active_agent_slug: data.active_agent_slug || data.agent?.slug || null,
  active_agent_name: data.active_agent_name || data.agent?.name || null,
  customer_memberships: data.customer_memberships || [],
  agent_access: data.agent_access || [],
});

const persistAdminAuthPayload = (data: any) => {
  if (!data?.token) {
    return sessionFromAuthPayload(data || {});
  }
  window.sessionStorage.setItem('admin_token', data.token);
  window.sessionStorage.setItem('admin_username', data.username || '');
  const session = sessionFromAuthPayload(data);
  window.sessionStorage.setItem('admin_session', JSON.stringify(session));
  return session;
};

const LoginPage: React.FC<LoginPageProps> = ({ isDarkMode }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerSlug = sanitizeWorkspaceSlugInput(searchParams.get('customer'));
  const redirectPath = safeRedirectPath(searchParams.get('redirect'));
  const [identifier, setIdentifier] = useState(searchParams.get('identifier') || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const targetLabel = useMemo(() => {
    if (customerSlug) {
      return `Dashboard ${customerSlug}`;
    }
    return 'Ihr Dashboard';
  }, [customerSlug]);

  const pageClasses = isDarkMode
    ? 'bg-slate-950 text-slate-50'
    : 'bg-slate-100 text-slate-950';
  const panelClasses = isDarkMode
    ? 'border-white/10 bg-slate-900 text-slate-100'
    : 'border-slate-200 bg-white text-slate-950';
  const inputClasses = isDarkMode
    ? 'border-slate-700 bg-slate-950 text-slate-100 focus:border-blue-400'
    : 'border-slate-300 bg-white text-slate-950 focus:border-blue-500';
  const mutedClasses = isDarkMode ? 'text-slate-300' : 'text-slate-500';
  const linkClasses = isDarkMode ? 'font-semibold hover:text-white' : 'font-semibold hover:text-slate-900';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      let loginResponse = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim(), password }),
      });
      let loginPayload = await loginResponse.json().catch(() => null);
      if (!loginResponse.ok) {
        const adminLoginResponse = await fetch(`${API_BASE}/api/admin/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: identifier.trim(), password }),
        });
        const adminLoginPayload = await adminLoginResponse.json().catch(() => null);
        if (adminLoginResponse.ok) {
          loginResponse = adminLoginResponse;
          loginPayload = adminLoginPayload;
        }
      }
      if (!loginResponse.ok) {
        throw new Error(loginPayload?.error || 'Login fehlgeschlagen.');
      }

      const session = persistAdminAuthPayload(loginPayload);
      const agentAccess = Array.isArray(session.agent_access) ? session.agent_access : [];
      const memberships = Array.isArray(session.customer_memberships) ? session.customer_memberships : [];
      const isEndCustomer = session.role === 'customeruser'
        || (memberships.some((membership: any) => membership?.role === 'customeruser')
          && !memberships.some((membership: any) => membership?.role === 'customeradmin'));
      if (isEndCustomer) {
        const firstGrant = agentAccess.find((grant: any) => grant?.status === 'active');
        if (!firstGrant?.customer_slug || !firstGrant?.agent_slug) {
          throw new Error('Für dieses Konto ist aktuell kein Avatar freigegeben.');
        }
        navigate(
          `/agent/${sanitizeWorkspaceSlug(firstGrant.customer_slug)}/${sanitizeWorkspaceSlug(firstGrant.agent_slug)}?voice=live`,
          { replace: true },
        );
        return;
      }
      const activeCustomerSlug = session.active_customer_slug ? sanitizeWorkspaceSlug(session.active_customer_slug) : '';
      let finalCustomerSlug = customerSlug || activeCustomerSlug;

      if (customerSlug) {
        const scopeResponse = await fetch(`${API_BASE}/api/admin/scope`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${loginPayload.token}`,
          },
          body: JSON.stringify({
            customerSlug,
            workspaceSlug: 'main',
            agentSlug: 'sally',
          }),
        });
        const scopePayload = await scopeResponse.json().catch(() => null);
        if (!scopeResponse.ok) {
          throw new Error(scopePayload?.error || 'Dieser Account hat keinen Zugriff auf diesen Customer.');
        }
        persistAdminAuthPayload(scopePayload);
        finalCustomerSlug = customerSlug;
      }

      navigate(redirectPath || (finalCustomerSlug ? `/dashboard/${finalCustomerSlug}` : '/admin'), { replace: true });
    } catch (submitError: any) {
      setError(submitError.message || 'Login fehlgeschlagen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className={`min-h-screen px-4 py-8 sm:px-6 lg:px-8 ${pageClasses}`}>
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-xl flex-col justify-center">
        <a href="/" className={`mb-6 inline-flex items-center gap-2 text-sm font-semibold ${mutedClasses}`}>
          <ArrowLeft className="h-4 w-4" />
          Zur Synaworks.ai
        </a>

        <section className={`rounded-lg border p-6 shadow-sm sm:p-8 ${panelClasses}`}>
          <div className="mb-8">
            <p className={`text-xs font-semibold uppercase tracking-[0.28em] ${mutedClasses}`}>Login</p>
            <h1 className="mt-3 text-3xl font-semibold">Einloggen</h1>
            <p className={`mt-3 text-sm leading-6 ${mutedClasses}`}>
              Melden Sie sich mit Ihrem Username an. Danach öffnen wir direkt {targetLabel}.
            </p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <label className="block space-y-2">
              <span className="flex items-center gap-2 text-sm font-medium">
                <User className="h-4 w-4 text-blue-500" />
                Username
              </span>
              <input
                className={`w-full rounded-lg border px-4 py-3 text-sm outline-none transition ${inputClasses}`}
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                autoComplete="username"
                required
              />
            </label>

            <label className="block space-y-2">
              <span className="flex items-center gap-2 text-sm font-medium">
                <Lock className="h-4 w-4 text-blue-500" />
                Passwort
              </span>
              <input
                className={`w-full rounded-lg border px-4 py-3 text-sm outline-none transition ${inputClasses}`}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                type="password"
                required
              />
            </label>

            {error ? <p className="text-sm text-red-500">{error}</p> : null}

            <button
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
              type="submit"
              disabled={isSubmitting}
            >
              <LogIn className="h-4 w-4" />
              {isSubmitting ? 'Login läuft...' : 'Einloggen'}
            </button>
          </form>

          <div className={`mt-5 flex flex-wrap items-center justify-between gap-3 text-sm ${mutedClasses}`}>
            <Link to="/forgot-password" className={linkClasses}>
              Passwort vergessen
            </Link>
            <Link to="/setup" className={linkClasses}>
              Neuen Agenten erstellen
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
};

export default LoginPage;
